import React from 'react';

import { readFolderCache, writeFolderCache } from './folderCache.js';
import { newestStudies } from './studyMetadata.js';

export default function useFolderPreset(key) {
  const [preset, setPreset] = React.useState(null);
  const [restoring, setRestoring] = React.useState(true);
  const [allowed, setAllowed] = React.useState(false);
  const [notice, setNotice] = React.useState('');
  const latestPreset = React.useRef(null);
  const writes = React.useRef(Promise.resolve());
  React.useEffect(() => {
    let cancelled = false;
    readFolderCache(key)
      .then(async (saved) => {
        if (!saved?.directory || !Array.isArray(saved.entries)) return;
        if (key === 'studies') saved.entries = newestStudies(saved.entries);
        const permission = await saved.directory.queryPermission({ mode: 'read' });
        if (!cancelled) {
          latestPreset.current = saved;
          setPreset(saved);
          setAllowed(permission === 'granted');
        }
      })
      .catch(() => {
        if (!cancelled)
          setNotice('Saved folders are unavailable in this browser. Select a folder to continue.');
      })
      .finally(() => {
        if (!cancelled) setRestoring(false);
      });
    return () => {
      cancelled = true;
    };
  }, [key]);
  const save = React.useCallback(
    (directory, entries, label) => {
      const saved = {
        directory,
        entries,
        label: label ?? latestPreset.current?.label ?? '',
        savedAt: Date.now(),
      };
      latestPreset.current = saved;
      setPreset(saved);
      setAllowed(true);
      // Serialize writes so a slower older scan cannot overwrite a newer label.
      writes.current = writes.current.catch(() => {}).then(() => writeFolderCache(key, saved));
      writes.current
        .then(() => setNotice(''))
        .catch(() =>
          setNotice(
            'This folder works for this session, but its preset could not be saved locally.'
          )
        );
    },
    [key]
  );
  const reconnect = async () => {
    try {
      const permission = await preset.directory.requestPermission({ mode: 'read' });
      setAllowed(permission === 'granted');
      setNotice(
        permission === 'granted'
          ? ''
          : 'Folder access was not granted. Reconnect or select the folder again.'
      );
    } catch {
      setNotice('This saved folder is unavailable. Select the folder again.');
    }
  };
  const setLabel = (label) => {
    if (!preset) return;
    const saved = { ...preset, label };
    latestPreset.current = saved;
    setPreset(saved);
    writes.current = writes.current.catch(() => {}).then(() => writeFolderCache(key, saved));
    writes.current.catch(() => setNotice('The folder label could not be saved locally.'));
  };
  return { preset, restoring, allowed, notice, save, reconnect, setLabel };
}
